import React, { useState, useEffect } from 'react';
import { OrgService, Organization } from 'app/services/OrgService';
import { SponsorService } from 'app/services/SponsorService';
import { Badge, Modal, ProgressBar, Button, Form } from 'react-bootstrap-v5';
import { VJFPUserService } from 'app/services/VJFPUserService';
import { VJFPUser } from 'app/API';
import { AdminRoutes } from './AdminPage';
import { useHistory } from 'react-router-dom';
import { AppTopNav } from "app/modules/shared/AppTopNav";
import { BasePageProps } from "app/types";
import moment from 'moment';

export const AdminOrganizations: React.FC<BasePageProps> = (props: BasePageProps) => {
    const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
    const [sponsors, setSponsors] = useState<{ id: string, name: string }[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
    const [hasMore, setHasMore] = useState(true);
    const [userDetails, setUserDetails] = useState<Record<string, VJFPUser>>({});
    const history = useHistory();

    const orgService = new OrgService();
    const sponsorService = new SponsorService();
    const vjfpUserService = new VJFPUserService();

    const loadOrganizations = async (loadMore = false) => {
        try {
            setIsLoading(true);
            const { organizations, cursor } = await orgService.getAllOrganizations(5, loadMore ? nextCursor : undefined);

            if (loadMore) {
                setAllOrgs(prev => [...prev, ...organizations]);
            } else {
                setAllOrgs(organizations);
            }

            setNextCursor(cursor);
            setHasMore(!!cursor);

            for (const org of organizations) {
                const userDetails = await vjfpUserService.getById(org.id, org.createdBy);
                setUserDetails(prev => ({ ...prev, [org.createdBy]: userDetails }));
            }
        } catch (error) {
            console.error('Error loading companies:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadSponsors = async () => {
        try {
            const { sponsors } = await sponsorService.getAllSponsors();
            setSponsors(sponsors.map(sponsor => ({ id: sponsor.id, name: sponsor.name })));
        } catch (error) {
            console.error('Error loading sponsors:', error);
        }
    };

    const handleSponsorChange = async (orgId: string, sponsorId: string) => {
        try {
            const selectedSponsor = sponsors.find(sponsor => sponsor.id === sponsorId);
            if (selectedSponsor) {
                await orgService.updateOrg(orgId, {
                    sponsorId: selectedSponsor.id,
                    sponsorName: selectedSponsor.name
                });
                setAllOrgs(prevOrgs =>
                    prevOrgs.map(org =>
                        org.id === orgId
                            ? { ...org, sponsorId: selectedSponsor.id, sponsorName: selectedSponsor.name }
                            : org
                    )
                );
            }
        } catch (error) {
            console.error('Error updating  company with sponsor:', error);
        }
    };

    useEffect(() => {
        loadOrganizations();
        loadSponsors();
    }, []);

    return (
        <div className="chatPage">
            <AppTopNav title={props.title} breadcrumbs={props.breadcrumbs} />
            <div className="container-fluid mt-10">
                <div className='row mb-5'>
                    <div className='col'>
                        <div className='card card-custom'>
                            <div className='card-body'>
                                <div className='table-responsive'>
                                    <table className='table table-hover table-bordered table-striped'>
                                        <thead className='table-dark'>
                                            <tr>
                                                <th>Company Name</th>
                                                <th>Address</th>
                                                <th>Created At</th>
                                                <th>Created By</th>
                                                <th>Contact Details</th>
                                                <th>Sponsor</th>
                                                <th>Jobfairs</th>
                                                <th>Onboarding status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {allOrgs.map(org => (
                                                <tr key={org.id}>
                                                    <td>{org.companyName}</td>
                                                    <td>{org.city}, {org.state}</td>
                                                    <td>{moment(org.createdAt).format('MMM DD YYYY, HH:mm A')}</td>
                                                    <td>{userDetails[org.createdBy]?.firstName || 'Loading...'} {userDetails[org.createdBy]?.lastName || ''}</td>
                                                    <td>
                                                        {userDetails[org.createdBy] && (
                                                            <>
                                                                <a href={'mailto:' + userDetails[org.createdBy].emailId}>{userDetails[org.createdBy].emailId}</a> | 
                                                                <a href={'tel:' + userDetails[org.createdBy].phone_number}>{userDetails[org.createdBy].phone_number}</a>
                                                            </>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Form.Select
                                                            value={org.sponsorId || ''}
                                                            onChange={(e) => handleSponsorChange(org.id, (e.target as HTMLSelectElement).value)}
                                                        >
                                                            <option value="">Select Sponsor</option>
                                                            {sponsors.map(sponsor => (
                                                                <option key={sponsor.id} value={sponsor.id}>
                                                                    {sponsor.name}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className='btn btn-primary btn-sm'
                                                            onClick={() => {
                                                                // Navigate to jobfairs page
                                                                history.push(AdminRoutes.Jobfairs + '?orgId=' + org.id);
                                                            }}
                                                        >
                                                            View Jobfairs
                                                        </button>
                                                    </td>
                                                    <td className='text-center align-middle'>
                                                        <ProgressBar className='mb-2' now={org.onBoardingProgress.progress} label={`${org.onBoardingProgress.progress}%`} />
                                                        {org.onBoardingProgress.lastCompletedStep === 'FilledOrganizationDetails' && <Badge bg='danger' className='align-self-center mx-auto' pill={true} text='white'>Filled  company details</Badge>}
                                                        {org.onBoardingProgress.lastCompletedStep === 'CreatedJobFair' && <Badge bg='warning' pill={true} text='white'>Job fair created</Badge>}
                                                        {org.onBoardingProgress.lastCompletedStep === 'JobFairPaymentCompleted' && <Badge bg='light-primary' pill={true} text='white'>Job fair payment completed</Badge>}
                                                        {org.onBoardingProgress.lastCompletedStep === 'BoothConfigured' && <Badge bg='light-primary' pill={true} text='white'>Booth configured</Badge>}
                                                        {org.onBoardingProgress.lastCompletedStep === 'AssignedARecruiter' && <Badge bg='success' pill={true} className='align-self-center mx-auto' text='white'>Assigned recruiter(s)</Badge>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {hasMore && (
                                    <div className='text-center mt-3'>
                                        <Button onClick={() => loadOrganizations(true)} disabled={isLoading}>
                                            {isLoading ? 'Loading...' : 'Load More'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <Modal show={showModal} onHide={() => setShowModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Company</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                    </Modal.Body>
                </Modal>
            </div>
        </div>
    );
};